import './belege.css';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { t, tx } from '../../i18n';
import { useToast } from '../../lib/toast';
import { useClientApp } from '../context';
import { EMPLOYEE_NOTES, notificationTarget } from './labels';
import { useHistoryWindow } from './nav';
import { useNotifications } from './shared';
import { Sheet } from './Sheet';
import type { NotificationItem } from './types';

// Glocke in der Kopfzeile: Zahl der neuen Benachrichtigungen und Liste (höchstens 40, neue zuerst).
// Kommt eine neue Benachrichtigung live an, gibt es eine kurze Meldung unten am Bildschirm.
// Die offene Glocke hat einen eigenen Verlaufseintrag: Zurück schließt nur die Glocke.
export function Bell() {
  const toast = useToast();
  const navigate = useNavigate();
  const n = useNotifications();
  const win = useHistoryWindow('glocke');
  const unread = n.data?.unread ?? 0;

  // Meldung nur für Einträge, die nach dem ersten Laden dazukommen. Die Liste zeigt höchstens 40, ungelesene zuerst:
  // Wird eine gelesen, rutscht eine ältere nach, die keine Meldung bekommt (nur neuer als die neueste bekannte).
  const known = useRef<{ ids: Set<string>; newest: string } | null>(null);
  useEffect(() => {
    const items = n.data?.items;
    if (!items) return;
    const newest = items.reduce((m, i) => (i.createdAt > m ? i.createdAt : m), known.current?.newest ?? '');
    if (!known.current) {
      known.current = { ids: new Set(items.map((i) => i.id)), newest };
      return;
    }
    const seen = known.current;
    const fresh = items.filter((i) => !i.read && !seen.ids.has(i.id) && i.createdAt > seen.newest);
    for (const i of items) seen.ids.add(i.id);
    seen.newest = newest;
    const top = fresh[0];
    if (top) toast(toastText(top));
  }, [n.data, toast]);

  return (
    <>
      <button
        type="button"
        className="bellbtn"
        aria-label={unread ? t('Benachrichtigungen, {n} neu', { n: unread }) : t('Benachrichtigungen')}
        aria-haspopup="dialog"
        onClick={() => {
          win.open();
          void n.reload();
        }}
      >
        🔔
        {unread > 0 && (
          <span className="cb-n" aria-hidden="true">
            {unread}
          </span>
        )}
      </button>
      {win.isOpen && (
        <BellSheet
          items={n.data?.items ?? []}
          unread={unread}
          onClose={win.close}
          // Ziel ersetzt den Eintrag der Glocke; Zurück führt danach auf die Seite vor der Glocke
          onGo={(target) => (target ? navigate(target, { replace: true }) : win.close())}
        />
      )}
    </>
  );
}

// Kurze Meldung unten am Bildschirm für eine neue Benachrichtigung (Titel und Notiz kommen Deutsch vom Server)
function toastText(n: NotificationItem): string {
  if (n.type === 'beleg_comment') {
    return n.author !== null
      ? t('Neuer Kommentar von {name} zu Ihrem Beleg', { name: n.author })
      : t('Neuer Kommentar von Ihrer Kanzlei zu Ihrem Beleg');
  }
  if (n.type === 'brief_comment') {
    return n.author !== null
      ? t('Neuer Kommentar von {name} zu Ihrem Brief', { name: n.author })
      : t('Neuer Kommentar von Ihrer Kanzlei zu Ihrem Brief');
  }
  if (n.type === 'beleg_fehlt') {
    return n.note !== null ? t('Ihre Kanzlei braucht noch: {note}', { note: n.note }) : t('Ihre Kanzlei braucht noch: Belege');
  }
  return tx(n.title);
}

function BellSheet({
  items,
  unread,
  onClose,
  onGo,
}: {
  items: NotificationItem[];
  unread: number;
  onClose(): void;
  onGo(target: string | null): void;
}) {
  return (
    <Sheet title={t('Benachrichtigungen')} onClose={onClose}>
      <div className="meta">{t('{n} neu · ältere bleiben hier gespeichert', { n: unread })}</div>
      <div className="bell-list">
        {items.length ? (
          items.map((i) => <NoteRow key={i.id} item={i} onGo={onGo} />)
        ) : (
          <div className="empty">{t('Noch keine Benachrichtigungen.')}</div>
        )}
      </div>
      <div className="src">{t('Neue Benachrichtigungen stehen auch oben auf der Übersicht, bis Sie sie öffnen.')}</div>
    </Sheet>
  );
}

// Eine Zeile wie auf der Übersicht: Symbol, Titel, Zeile darunter, Knopf.
// onGo übernimmt das Weiterleiten (in der Glocke), sonst öffnet der Knopf das Ziel selbst.
export function NoteRow({ item, onGo, firm }: { item: NotificationItem; onGo?(target: string | null): void; firm?: boolean }) {
  const navigate = useNavigate();
  const { clients } = useClientApp();
  const n = useNotifications();
  const target = notificationTarget(item);
  const comment = item.type === 'beleg_comment' || item.type === 'brief_comment' || item.type === 'anfrage_comment';
  const showFirm = firm ?? clients.length > 1;
  const label = !target
    ? t('Ok')
    : item.type === 'ustva_filed'
      ? t('Protokoll ansehen')
      : item.type === 'ustva_pruefen'
        ? t('Prüfen und senden')
      : item.type === 'bwa_uploaded'
        ? t('BWA ansehen')
        : item.type === 'unterschrift'
          ? item.read
            ? t('Ansehen')
            : t('Ansehen & unterschreiben')
          : item.type === 'kanzlei_dokument' || item.type === 'jahresabschluss' || item.type.startsWith('rechnung') || item.type.startsWith('angebot_')
            ? t('Ansehen')
            : comment
              ? item.read
                ? t('Öffnen')
                : t('Ansehen & antworten')
              : item.type === 'lohn_unterlagen' || item.type === 'anfrage_datei' || item.type === 'brief_neu'
                ? t('Ansehen')
                : EMPLOYEE_NOTES.has(item.type) || item.type === 'brief_erledigt' || item.type === 'anfrage_erledigt'
                  ? t('Öffnen')
                  : t('Jetzt hochladen');
  const icon = comment
    ? '💬'
    : item.type === 'brief_neu'
      ? '✉️'
      : item.type === 'ustva_filed' || item.type === 'ustva_pruefen'
        ? '🧾'
        : item.type === 'bwa_uploaded'
          ? '📊'
          : item.type.startsWith('rechnung') || item.type.startsWith('angebot_')
            ? '💶'
            : item.type === 'unterschrift'
              ? '✍️'
              : item.type === 'lohn_unterlagen' ||
                  item.type === 'anfrage_datei' ||
                  item.type === 'kanzlei_dokument' ||
                  item.type === 'jahresabschluss'
                ? '📄'
                : item.type === 'kuendigung_korrigiert'
                  ? '📅'
                  : item.type === 'kuendigung_storniert'
                    ? '↩️'
                    : EMPLOYEE_NOTES.has(item.type) || item.type === 'brief_erledigt' || item.type === 'anfrage_erledigt'
                      ? '✅'
                      : '📎';

  const go = async () => {
    // „Bitte unterschreiben“ bleibt ungelesen, bis unterschrieben ist (das erledigt der Server)
    if (!item.read && item.type !== 'unterschrift') {
      await api.post(`/api/app/notifications/${item.id}/read`).catch(() => {});
      void n.reload();
    }
    if (onGo) onGo(target);
    else if (target) navigate(target);
  };

  return (
    <div className={`nt-r${item.read ? ' read' : ''}`}>
      <span className="nt-i" aria-hidden="true">
        {icon}
      </span>
      <div className="tx">
        <b>{tx(item.title)}</b>
        {(item.body || showFirm) && (
          <small>
            {item.body && tx(item.body)}
            {showFirm ? `${item.body ? ' · ' : ''}${item.clientName}` : ''}
          </small>
        )}
      </div>
      <button type="button" className="btn2 dark" onClick={go}>
        {label}
      </button>
    </div>
  );
}
